import { Button, Icons, useToggle } from '@commenda/kakashi';
import {
  TaskParty,
  TaskStatus,
} from '@prisma/client';
import { ServiceRequestTaskLabel } from '../../../common/labels';
import { ServiceRequestTaskIconConfig } from '../../../common/taskIconConfig';
import { ServiceRequestTaskCard } from '../../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../../types';
import { updateTaskStatus } from '../../../utils';
import { DirectorDetails, DirectorDetailsModal } from './DirectorDetailsModal';

const DSC_VENDOR_CALENDAR_LINK = 'https://cal.com/akhilesh-dixit/30min';

export const ScheduleMeetingTask = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, mutate, serviceRequest } = context;

  const directorDetailsModalVisibility = useToggle();

  const directorDetails = task.metadata
    ? (task.metadata as { directorDetails?: DirectorDetails }).directorDetails
    : undefined;

  const directorId = task.metadata
    ? (task.metadata as { directorId?: number }).directorId
    : undefined;

  const director = directorId
    ? serviceRequest.corporation?.corporationHasDirector?.find(
        (chd) => chd.director.id === directorId
      )?.director
    : undefined;

  const computedDirectorDetails: DirectorDetails | undefined = directorDetails
    ? directorDetails
    : director
      ? {
          directorFirstName: director.directorFirstName,
          directorMiddleName: director.directorMiddleName ?? undefined,
          directorLastName: director.directorLastName,
          number: (director as unknown as { number?: string }).number ?? 'N/A',
          email: (director as unknown as { email?: string }).email ?? 'N/A',
          personalDetails: (
            director as unknown as {
              personalDetails?: DirectorDetails['personalDetails'];
            }
          ).personalDetails,
        }
      : undefined;

  const markCallBooked = async () => {
    await updateTaskStatus({
      task,
      status: TaskStatus.COMPLETED,
      mutate,
    });
  };

  const handleBookCall = () => {
    if (computedDirectorDetails) {
      directorDetailsModalVisibility.show();
    } else {
      window.open(DSC_VENDOR_CALENDAR_LINK, '_blank');
    }
  };

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.CLIENT]: {
          [TaskStatus.TO_DO]: {
            actions: (
              <Button size="sm" onClick={handleBookCall} variant="primary">
                <Icons.Calendar className="w-4 h-4" />
                Book a call
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel label="Schedule DSC procurement meeting" />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: (
              <Button size="sm" disabled variant="primary">
                <Icons.Calendar className="w-4 h-4" />
                Book a call
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel label="Schedule DSC procurement meeting" />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: computedDirectorDetails ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={directorDetailsModalVisibility.show}
              >
                <Icons.Eye className="w-4 h-4" />
                View Details
              </Button>
            ) : null,
            label: (
              <ServiceRequestTaskLabel
                label="DSC meeting scheduled"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
        [TaskParty.AGENT]: {
          [TaskStatus.TO_DO]: {
            actions: (
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={handleBookCall} variant="secondary">
                  <Icons.Calendar className="w-4 h-4" />
                  Book a call
                </Button>
                <Button size="sm" onClick={markCallBooked} variant="primary">
                  <Icons.Check className="w-4 h-4" />
                  Mark as completed
                </Button>
              </div>
            ),
            label: (
              <ServiceRequestTaskLabel label="Schedule DSC procurement meeting" />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Schedule DSC procurement meeting" />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: computedDirectorDetails ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={directorDetailsModalVisibility.show}
              >
                <Icons.Eye className="w-4 h-4" />
                View Details
              </Button>
            ) : null,
            label: (
              <ServiceRequestTaskLabel
                label="DSC meeting scheduled"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{
          role,
          status: task.status,
        }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
      {computedDirectorDetails && (
        <DirectorDetailsModal
          isOpen={directorDetailsModalVisibility.visible}
          onClose={directorDetailsModalVisibility.hide}
          calendarLink={DSC_VENDOR_CALENDAR_LINK}
          serviceRequest={serviceRequest}
          directorId={directorId}
          isViewOnly={task.status === TaskStatus.COMPLETED}
        />
      )}
    </div>
  );
};
